import { useState } from 'react';

import { DOCUMENT_SEARCH_ORIGIN } from '@/features/DocumentSearch/documentSearchOrigin';
import {
    useDocument,
    useUpdateDocumentStatus,
} from '@/features/end-of-lease/api';
import { CommentDialog } from '@/features/end-of-lease/comment-dialog';
import { DocumentStatusBadge } from '@/features/end-of-lease/document-status-badge';
import EOPreview from '@/features/end-of-lease/eo-preview';
import { useCommentDialog } from '@/features/end-of-lease/hooks/useCommentDialog';
import Preview from '@/features/end-of-lease/preview';
import { DocumentStatus } from '@/features/end-of-lease/types';
import { createFileRoute } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CircleX, Info, Lock, PanelRight, X } from 'lucide-react';
import { toast } from 'sonner';

import { LIST_VIEW_RETURN_KEY } from '@/lib/listViewState';
import { formatDate } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';

// TODO: Move this to an actual index route documents/:id
export const Route = createFileRoute('/lease/documents/$documentId/page')({
    component: DocumentDetailPage,
    validateSearch: (search: Record<string, unknown>): { from?: string } => ({
        from: search.from === DOCUMENT_SEARCH_ORIGIN ? search.from : undefined,
    }),
});

export function DocumentDetailPage() {
    const navigate = useNavigate();
    const { from } = Route.useSearch();
    const cameFromSearch = from === DOCUMENT_SEARCH_ORIGIN;
    // Returns to Document Search with the restore marker, bringing back its search and page.
    const backToSearch = () =>
        navigate({
            to: '/meta-search',
            search: { [LIST_VIEW_RETURN_KEY]: true },
        } as unknown as Parameters<typeof navigate>[0]);
    const { documentId: _documentId } = Route.useParams();
    const documentId = Number(_documentId);
    const [showDetails, setShowDetails] = useState(false);

    const { data: document } = useDocument(documentId);
    const updateDocumentStatus = useUpdateDocumentStatus(documentId);

    const handleUpdateStatus = async (data: {
        status: DocumentStatus;
        comment: string;
    }) => {
        toast.promise(updateDocumentStatus.mutateAsync(data), {
            loading: 'Updating status...',
            success: 'Status updated successfully',
            error: 'Failed to update status',
        });
    };

    const {
        dialogState,
        openDialog,
        closeDialog,
        isCommentRequired,
        canSubmit,
        handleSubmit,
        handleCommentChange,
    } = useCommentDialog<
        DocumentStatus,
        { status: DocumentStatus; comment: string }
    >({
        onSubmit: handleUpdateStatus,
        requiresCommentStatuses: [DocumentStatus.requires_attention],
        createSubmitData: (status: DocumentStatus, comment: string) => ({
            status,
            comment,
        }),
    });

    const hasConnectedDocuments =
        (document?.connectedDocuments?.filter((cd) => cd.id != document.id)
            ?.length || 0) > 0;

    const showVerifyReview = document?.meta?.raw?.type == 'ServiceBulletin';

    if (!document || document?.meta?.raw === null) {
        return (
            <div className="flex-1 h-full flex items-center justify-center">
                <Card className="w-96">
                    <CardHeader>
                        <CardTitle>Document not found</CardTitle>
                        <CardDescription>
                            The requested document could not be loaded
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            variant="outline"
                            onClick={
                                cameFromSearch
                                    ? backToSearch
                                    : () => navigate({ to: '/lease' })
                            }
                        >
                            {cameFromSearch
                                ? 'Back to Document Search'
                                : 'Back to Dashboard'}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div
            className="flex-1 h-full overflow-hidden flex flex-col"
            data-testid="LeaseDocumentDetail"
        >
            <div className="flex flex-1 overflow-hidden h-full">
                {/* Main content area */}
                <div className="flex-1 flex flex-col h-full overflow-y-auto p-6">
                    {cameFromSearch && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="mb-4 self-start"
                            onClick={backToSearch}
                            data-testid="LeaseDocumentDetail__backToSearch"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Document Search
                        </Button>
                    )}
                    {/* Document header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <DocumentStatusBadge
                                size="sm"
                                mode="large"
                                status={
                                    document?.meta?.status ??
                                    DocumentStatus.pending
                                }
                            />
                            <h1 className="text-xl max-w-3xl font-semibold truncate">
                                {document?.meta?.raw?.title ||
                                    `Service Bulletin ${document?.meta?.raw?.id}`}
                            </h1>
                        </div>

                        <div className="flex gap-2">
                            {showVerifyReview && (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() =>
                                            openDialog(DocumentStatus.verified)
                                        }
                                        className="gap-2"
                                        disabled={
                                            document?.meta?.status ==
                                            DocumentStatus.verified
                                        }
                                    >
                                        <Lock className="size-4 text-green-500" />
                                        Verify
                                    </Button>

                                    <Button
                                        variant="outline"
                                        onClick={() =>
                                            openDialog(
                                                DocumentStatus.requires_attention,
                                            )
                                        }
                                        className="gap-2"
                                        disabled={
                                            document?.meta?.status ==
                                            DocumentStatus.requires_attention
                                        }
                                    >
                                        <CircleX className="size-4 text-red-500" />
                                        Needs Review
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="outline"
                                onClick={() => setShowDetails(!showDetails)}
                                className="gap-2"
                            >
                                <Info className="size-4" />
                                Details
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-row gap-4 h-full w-full">
                        {/* Connected documents section - Left 30% */}
                        {hasConnectedDocuments && (
                            <div className="w-[30%] h-full overflow-y-auto border-r pr-4">
                                <h3 className="text-lg font-semibold mb-4">
                                    Connected Documents
                                </h3>
                                <div className="space-y-4">
                                    {document?.connectedDocuments
                                        ?.filter((cd) => cd.id != document.id)
                                        .map((cd) => (
                                            <EOPreview
                                                key={cd.id}
                                                document={cd}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Document preview - Remaining width */}
                        <div
                            className={`h-full ${hasConnectedDocuments ? 'flex-1' : 'w-full'}`}
                        >
                            <Preview docId={document.id} />
                        </div>
                    </div>
                </div>

                {/* Side panel for details */}
                {showDetails && (
                    <div className="w-96 h-full border-l bg-background p-6 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold">
                                Document Details
                            </h2>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowDetails(false)}
                            >
                                <X className="size-4" />
                            </Button>
                        </div>

                        <Collapsible className="mb-4" defaultOpen>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between w-full p-3 bg-muted rounded-md font-medium">
                                    Aircraft Information
                                    <PanelRight className="size-4" />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="p-3 pt-4 space-y-2 text-sm">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Type
                                    </span>
                                    <span className="font-medium">
                                        {document?.meta?.raw?.aircraft?.type}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Registration
                                    </span>
                                    <span className="font-medium">
                                        {
                                            document?.meta?.raw?.aircraft
                                                ?.registration
                                        }
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Serial Number
                                    </span>
                                    <span className="font-medium">
                                        {
                                            document?.meta?.raw?.aircraft
                                                ?.serialNumber
                                        }
                                    </span>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>

                        <Collapsible className="mb-4" defaultOpen>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between w-full p-3 bg-muted rounded-md font-medium">
                                    Compliance Information
                                    <PanelRight className="size-4" />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="p-3 pt-4 space-y-2 text-sm">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Category
                                    </span>
                                    <Badge variant="outline">
                                        {
                                            document?.meta?.raw?.compliance
                                                ?.category
                                        }
                                    </Badge>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Timeframe
                                    </span>
                                    <span className="font-medium">
                                        {
                                            document?.meta?.raw?.compliance
                                                ?.timeframe
                                        }
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Authority
                                    </span>
                                    <span className="font-medium">
                                        {document?.meta?.raw?.issuingAuthority}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Issue Date
                                    </span>
                                    <span className="font-medium">
                                        {formatDate(
                                            document?.meta?.raw?.issueDate,
                                        )}
                                    </span>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>

                        <Collapsible className="mb-4" defaultOpen>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between w-full p-3 bg-muted rounded-md font-medium">
                                    Manpower Requirements
                                    <PanelRight className="size-4" />
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="p-3 pt-4 space-y-2 text-sm">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Estimated Hours
                                    </span>
                                    <span className="font-medium">
                                        {document?.meta?.raw
                                            ?.estimatedManHours || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">
                                        Actual Hours
                                    </span>
                                    <span className="font-medium">
                                        {document?.meta?.raw?.actualManHours ||
                                            'Not recorded'}
                                    </span>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>

                        {(document?.meta?.raw?.approvals?.length ?? 0) > 0 && (
                            <Collapsible className="mb-4">
                                <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between w-full p-3 bg-muted rounded-md font-medium">
                                        Approvals
                                        <PanelRight className="size-4" />
                                    </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="p-3 pt-4 space-y-3 text-sm">
                                    {document?.meta?.raw?.approvals?.map(
                                        (approval, i) => (
                                            <div
                                                key={i}
                                                className="border rounded-md p-2"
                                            >
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">
                                                        Role
                                                    </span>
                                                    <span className="font-medium">
                                                        {approval.role}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">
                                                        Name
                                                    </span>
                                                    <span className="font-medium">
                                                        {approval.name}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">
                                                        Signed
                                                    </span>
                                                    <Badge
                                                        variant={
                                                            approval.signed
                                                                ? 'default'
                                                                : 'outline'
                                                        }
                                                    >
                                                        {approval.signed
                                                            ? 'Yes'
                                                            : 'No'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </CollapsibleContent>
                            </Collapsible>
                        )}
                    </div>
                )}
            </div>

            <CommentDialog
                open={dialogState.open}
                comment={dialogState.comment}
                isCommentRequired={isCommentRequired}
                canSubmit={canSubmit}
                onCancel={closeDialog}
                onSubmit={handleSubmit}
                onCommentChange={handleCommentChange}
            />
        </div>
    );
}
